import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import Loader from 'components/Loader';
import { fetchCollateralSummary as fetchCollateralSummaryAction } from 'actions/dashboard';
import {
  formatLtvMetricDisplay,
  getCollateralSummaryHeldDisplay,
  getTargetLtvDisplay,
  getDynamicLtvTierState,
  shouldShowPartialLiquidationBanner,
  toLtvPercentage,
} from '../utils/cryptoUtils';
import './CollateralSummaryCard.scss';

const THRESHOLD_LABEL_MIN_GAP_PCT = 16;
const THRESHOLD_LABEL_COMPACT_TITLES = {
  target: 'contract LTV',
  margin: 'margin call LTV',
  liquidation: 'liquidation LTV',
};

const applyThresholdLabelSpread = (ticks) => {
  const layout = ticks.map((tick) => ({
    ...tick,
    title: tick.title,
  }));

  for (let index = 0; index < layout.length - 1; index += 1) {
    const leftTick = layout[index];
    const rightTick = layout[index + 1];
    const gap = rightTick.left - leftTick.left;

    if (gap < THRESHOLD_LABEL_MIN_GAP_PCT) {
      leftTick.title = THRESHOLD_LABEL_COMPACT_TITLES[leftTick.key] || leftTick.title;
      rightTick.title = THRESHOLD_LABEL_COMPACT_TITLES[rightTick.key] || rightTick.title;
    }
  }

  return layout;
};

const buildThresholdTickLayout = ({
  targetLtvLeft,
  targetLtvTrackLabel,
  marginCallLeft,
  marginCallLabel,
  marginCallPct,
  liquidationLeft,
  liquidationLabel,
  liquidationPct,
}) => {
  const ticks = [];

  if (targetLtvLeft != null) {
    ticks.push({
      key: 'target',
      variant: 'target',
      left: targetLtvLeft,
      title: 'contract LTV',
      value: `${targetLtvTrackLabel}%`,
    });
  }
  if (marginCallPct != null) {
    ticks.push({
      key: 'margin',
      variant: 'margin',
      left: marginCallLeft,
      title: 'margin call',
      value: `${marginCallLabel}%`,
    });
  }
  if (liquidationPct != null) {
    ticks.push({
      key: 'liquidation',
      variant: 'liquidation',
      left: liquidationLeft,
      title: 'liquidation',
      value: `${liquidationLabel}%`,
    });
  }

  const sorted = [...ticks].sort((a, b) => a.left - b.left);

  const spreadTicks = applyThresholdLabelSpread(sorted);

  return {
    ticks: spreadTicks,
    labelAreaPadding: 68,
  };
};

const StatusWarningIcon = ({ className = '' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className={`cs-card__badge-icon ${className}`.trim()}>
    <path
      d="M12 4 3 20h18L12 4Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1.2" fill="currentColor" />
  </svg>
);

StatusWarningIcon.propTypes = {
  className: PropTypes.string,
};

StatusWarningIcon.defaultProps = {
  className: '',
};

const CollateralSummaryCard = ({
  loanId,
  loanTerm,
  loanDetail,
  collateralSummary,
  fetchCollateralSummary,
  onLowerLtv,
}) => {
  useEffect(() => {
    if (!loanId) {
      return;
    }

    fetchCollateralSummary(loanId);
  }, [loanId, fetchCollateralSummary]);

  const isLoading = loanTerm?.isLoading || collateralSummary?.isLoading;

  if (isLoading) {
    return <Loader className="height-2" />;
  }

  const currentLtvRaw = get(collateralSummary, 'data.currentLtv');
  const marginCallRaw = get(loanTerm, 'data.marginCallLtv');
  const liquidationRaw = get(loanTerm, 'data.liquidationLtv');

  const currentLtvPct = toLtvPercentage(currentLtvRaw);
  const marginCallPct = toLtvPercentage(marginCallRaw);
  const liquidationPct = toLtvPercentage(liquidationRaw);
  const { targetLtvPct: contractualLtvPct } = getTargetLtvDisplay(loanTerm);

  const {
    clampedLtv,
    marginCallThreshold,
    liquidationThreshold,
    isLiquidating,
    isCritical,
    isWarning,
    isAboveContractualLtv,
  } = getDynamicLtvTierState({
    loanToValuePct: currentLtvPct,
    marginCallPct,
    liquidationPct,
    contractualLtvPct,
  });

  // const dpdRaw = Number(get(loanDetail, 'data.0.daysPastDue', 0));
  const dpdRaw = Number(get(loanDetail, 12));
  const dpd = Number.isNaN(dpdRaw) ? 0 : dpdRaw;
  const isCollateralLiquidated = Boolean(get(loanDetail, 'data.0.isCollateralLiquidated', false));
  const liquidationAfterDays = Number(get(loanDetail, 'data.0.collateralLiquidationDaysPastDue', 45)) || 45;

  const isDpdPartialLiquidation = shouldShowPartialLiquidationBanner({
    isCryptoLoan: true,
    isCollateralLiquidated,
    daysPastDue: dpd,
    liquidationAfterDays,
  });

  const showAboveContractualLtvLayout = isAboveContractualLtv && !isDpdPartialLiquidation && !isLiquidating;
  const showMarginCallLayout = isWarning && !isDpdPartialLiquidation && !isLiquidating;
  const showCriticalLayout = isCritical && !isLiquidating;
  const showLiquidatingLayout = isLiquidating;
  const isAboveMarginCall = marginCallPct != null
    && currentLtvPct != null
    && currentLtvPct >= marginCallPct;
  const isAboveContractual = contractualLtvPct != null
    && currentLtvPct != null
    && currentLtvPct > contractualLtvPct;
  const showLowerLtvButton = (isAboveMarginCall || isAboveContractual)
    && !isCollateralLiquidated
    && !isDpdPartialLiquidation;

  const displayVariant = showLiquidatingLayout
    ? 'liquidating'
    : showCriticalLayout
      ? 'critical'
      : (showMarginCallLayout || showAboveContractualLtvLayout)
        ? 'warning'
        : 'safe';

  const barFillVariant = (() => {
    if (currentLtvPct == null) {
      return 'safe';
    }
    if (marginCallPct != null && clampedLtv >= marginCallThreshold) {
      return 'critical';
    }
    if (contractualLtvPct != null && clampedLtv > contractualLtvPct) {
      return 'warning';
    }
    return 'safe';
  })();

  const markerLeft = clampedLtv;
  const liquidationLeft = liquidationThreshold;
  const marginCallLeft = marginCallThreshold;
  const fillWidth = markerLeft;
  const marginCallLabel = marginCallPct == null ? '-' : Math.round(marginCallThreshold);
  const liquidationLabel = liquidationPct == null ? '-' : Math.round(liquidationThreshold);
  const targetLtvLeft = contractualLtvPct == null ? null : Math.max(0, Math.min(100, contractualLtvPct));
  const targetLtvTrackLabel = contractualLtvPct == null ? '-' : Math.round(contractualLtvPct);
  const { ticks: thresholdTicks, labelAreaPadding } = buildThresholdTickLayout({
    targetLtvLeft,
    targetLtvTrackLabel,
    marginCallLeft,
    marginCallLabel,
    marginCallPct,
    liquidationLeft,
    liquidationLabel,
    liquidationPct,
  });
  const inlineThresholdTicks = thresholdTicks.filter((tick) => tick.inline);
  const belowThresholdTicks = thresholdTicks.filter((tick) => !tick.inline);

  const summary = getCollateralSummaryHeldDisplay(collateralSummary?.data);
  const collateralHeld = summary.collateralHeld || '-';
  const currentLtvDisplay = formatLtvMetricDisplay(currentLtvRaw);

  const lowerLtvButtonLabel = isAboveMarginCall
    ? 'Resolve Liquidation'
    : 'Protect Your Collateral';

  const lowerLtvButtonClassName = `cs-card__lower-ltv-btn${
    isAboveMarginCall
      ? ' cs-card__lower-ltv-btn--critical'
      : ' cs-card__lower-ltv-btn--warning'
  }`;

  return (
    <div className={`cs-card cs-card--${displayVariant}`}>
      <div className="cs-card__header">
        <div className="cs-card__header-left">
          <h3 className="cs-card__title">Collateral Summary</h3>
          <p className="cs-card__subtitle">
            <span className="cs-card__btc-value">{collateralHeld}</span>
            {' held as collateral'}
          </p>
        </div>
        {showLowerLtvButton && typeof onLowerLtv === 'function' && (
          <button
            type="button"
            className={lowerLtvButtonClassName}
            onClick={onLowerLtv}
          >
            {lowerLtvButtonLabel}
          </button>
        )}
      </div>

      <div className="cs-card__divider" />

      <div className="cs-card__progress-section">
        <div className="cs-card__track-wrap">
          {currentLtvPct != null && (
          <div
            className="cs-card__marker-label"
            style={{ left: `${markerLeft}%` }}
          >
            <span className="cs-card__marker-text">
              {'you are here '}

              <strong>{currentLtvDisplay}</strong>
              {' LTV'}
            </span>
            <svg className="cs-card__marker-curve" width="28" height="22" viewBox="0 0 28 22" aria-hidden="true">
              <path
                d="M8 1 C12 5, 16 10, 14 21"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="2 2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          )}

          <div
            className="cs-card__track-ruler"
            style={{ paddingBottom: `${labelAreaPadding}px` }}
          >
            <div className="cs-card__track-row">
              <div className="cs-card__track">
                <div
                  className={`cs-card__track-fill cs-card__track-fill--${barFillVariant}`}
                  style={{ width: `${fillWidth}%` }}
                />
                {liquidationPct != null && (
                <div
                  className="cs-card__track-danger"
                  style={{ left: `${liquidationLeft}%`, width: `${Math.max(0, 100 - liquidationLeft)}%` }}
                />
                )}
                {inlineThresholdTicks.map((tick) => {
                  const isFillPastMarker = fillWidth >= tick.left;

                  return (
                    <div
                      key={tick.key}
                      className={[
                        'cs-card__threshold-marker',
                        'cs-card__threshold-marker--inline',
                        `cs-card__threshold-marker--${tick.variant}`,
                        isFillPastMarker ? 'cs-card__threshold-marker--inline-over-fill' : '',
                      ].join(' ')}
                      style={{ left: `${tick.left}%` }}
                    >
                      <span className="cs-card__threshold-marker-inline-label">{tick.value}</span>
                      <span className="cs-card__threshold-marker-line" />
                    </div>
                  );
                })}
                {currentLtvPct != null && (
                <div
                  className={`cs-card__diamond cs-card__diamond--${barFillVariant}`}
                  style={{ left: `${markerLeft}%` }}
                />
                )}
                {barFillVariant === 'safe' && currentLtvPct != null && (
                <div className="cs-card__track-glow cs-card__track-glow--safe" style={{ left: `${markerLeft}%` }} />
                )}
                {(showCriticalLayout || showLiquidatingLayout) && currentLtvPct != null && (
                <div className="cs-card__track-glow" style={{ left: `${markerLeft}%` }} />
                )}
              </div>
            </div>

            <div className="cs-card__threshold-markers" aria-hidden="true">
              {belowThresholdTicks.map((tick) => (
                <div
                  key={tick.key}
                  className={[
                    'cs-card__threshold-marker',
                    `cs-card__threshold-marker--${tick.variant}`,
                  ].join(' ')}
                  style={{ left: `${tick.left}%` }}
                >
                  <span className="cs-card__threshold-marker-line" />
                  <span className="cs-card__threshold-marker-label">
                    <span className="cs-card__threshold-marker-value">{tick.value}</span>
                    <span className="cs-card__threshold-marker-title">{tick.title}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CollateralSummaryCard.propTypes = {
  loanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loanTerm: PropTypes.shape({
    data: PropTypes.object,
    isLoading: PropTypes.bool,
  }),
  loanDetail: PropTypes.shape({
    data: PropTypes.array,
  }),
  collateralSummary: PropTypes.shape({
    data: PropTypes.object,
    isLoading: PropTypes.bool,
  }),
  fetchCollateralSummary: PropTypes.func.isRequired,
  onLowerLtv: PropTypes.func,
};

CollateralSummaryCard.defaultProps = {
  loanId: null,
  loanTerm: {},
  loanDetail: {},
  collateralSummary: {},
  onLowerLtv: undefined,
};

const mapStateToProps = (state) => ({
  loanTerm: state.dashboard.loanTerm,
  loanDetail: state.dashboard.loanDetail,
  collateralSummary: state.dashboard.collateralSummary,
});

export default compose(
  connect(mapStateToProps, {
    fetchCollateralSummary: fetchCollateralSummaryAction,
  })
)(CollateralSummaryCard);
